import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy, toEscaLang } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
interface NavItem {
  hash: string;
  label: string;
}
export function EscaHeader() {
  const {
    lang,
    setLang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const active = toEscaLang(lang);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const items: NavItem[] = [{
    hash: 'expertise',
    label: c.nav.expertise
  }, {
    hash: 'research',
    label: c.nav.research
  }, {
    hash: 'cases',
    label: c.nav.cases
  }, {
    hash: 'about',
    label: c.nav.about
  }];
  const go = (hash: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    document.getElementById(hash)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const onCta = (source: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    track('header_cta_click', {
      source
    });
    document.getElementById('discuss')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${scrolled ? 'bg-sand/95 backdrop-blur-md border-b border-line' : 'bg-transparent border-b border-transparent'}`} data-id="element-318" data-mp="1j0tylxcahvkn">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-319" data-mp="3ibcxj4s85sqq">
        <div className={`flex items-center justify-between transition-[height] duration-300 ${scrolled ? 'h-16' : 'h-20 lg:h-24'}`} data-id="element-320" data-mp="3fxpx8oz3iz9n">
          <a href="#top" onClick={go('top')} className="font-display font-bold tracking-[-0.04em] text-ink text-2xl lg:text-[1.75rem] leading-none" data-id="element-321" data-mp="36wib159utovx">
            ESCA
          </a>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation" data-id="element-322" data-mp="3g2tb3jkygad">
            {items.map(item => <a key={item.hash} href={`#${item.hash}`} onClick={go(item.hash)} className="text-[0.9375rem] text-muted hover:text-ink transition-colors duration-200" data-id="element-323" data-mp="3pve247qf1n5j">
                {item.label}
              </a>)}
          </nav>

          <div className="flex items-center gap-4 lg:gap-6" data-id="element-324" data-mp="hf2eg17vwd1s">
            <div className="hidden sm:flex items-center gap-1 text-sm" data-id="element-325" data-mp="hdhm8i91l673">
              {(['UA', 'EN'] as const).map((l, i) => <React.Fragment key={l} data-id="element-326" data-mp="tpk2p60hopbe" data-mpi={i}>
                  {i > 0 && <span className="text-line" data-id="element-327" data-mp="tq256kdqvzrr">/</span>}
                  <button type="button" onClick={() => setLang(l)} className={`px-1 transition-colors duration-200 ${active === l ? 'text-ink font-semibold' : 'text-muted hover:text-ink'}`} data-id="element-328" data-mp="yode8sw7u68x">
                    {l}
                  </button>
                </React.Fragment>)}
            </div>

            <a href="#discuss" onClick={onCta('header')} className="hidden sm:inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-accent hover:bg-accent-600 transition-colors duration-200" data-id="element-329" data-mp="1mehf4si0rrw8">
              {c.nav.cta}
            </a>

            <button type="button" onClick={() => setOpen(o => !o)} className="lg:hidden -mr-2 p-2 text-ink" aria-label={open ? c.nav.close : c.nav.menu} aria-expanded={open} data-id="element-330" data-mp="2t3sdevo91fcn">
              {open ? <XIcon className="w-6 h-6" data-id="element-331" data-mp="2xuqtbu5bsbfd" /> : <MenuIcon className="w-6 h-6" data-id="element-332" data-mp="2xvk4r8f8bp3u" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-333" data-mp="2a70voqocv8ap">
        {open && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="lg:hidden bg-sand border-t border-line" data-id="element-334" data-mp="2574suure9mwq">
            <nav className="px-5 py-6 flex flex-col" aria-label="Mobile navigation" data-id="element-335" data-mp="3ukhyqof5nizy">
              {items.map(item => <a key={item.hash} href={`#${item.hash}`} onClick={go(item.hash)} className="py-3.5 text-lg font-display font-semibold text-ink border-b border-line" data-id="element-336" data-mp="3poarmsb0w1gf">
                  {item.label}
                </a>)}

              <div className="flex items-center gap-2 pt-6 pb-5" data-id="element-337" data-mp="3t3m0c6ek2hab">
                {(['UA', 'EN'] as const).map(l => <button key={l} type="button" onClick={() => setLang(l)} className={`px-4 py-2 text-sm transition-colors duration-200 ${active === l ? 'bg-ink text-sand font-semibold' : 'text-muted border border-line'}`} data-id="element-338" data-mp="kp3p5vtbilmh">
                    {l}
                  </button>)}
              </div>

              <a href="#discuss" onClick={onCta('mobile_menu')} className="inline-flex items-center justify-center w-full px-6 py-4 text-base font-semibold text-white bg-accent" data-id="element-339" data-mp="18eoj19r0f3aj">
                {c.nav.cta}
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}